import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../../FirebaseConfig";
import useThemeState from "../../StatesStore/Theme";
import { toastOptions } from "../../lib/toastOptions";
import { usePostHog } from "posthog-js/react";
import { usePointsSystem } from "../../Points/usePointsSystem";

const AddToFavorites = ({
  list = null,
  listName = null,
  listId = null,
  productId,
  product = null,
  userId,
  type,
  componentName = null,
  width = 24,
  height = 24,
  borderRadius = 2

}) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const { awardPoints } = usePointsSystem(userId);
  const posthog = usePostHog()

  useEffect(() => {
    const checkIfFavorite = async () => {
      if (userId) {
        try {
          // Use productId if listId is not available
          const docId = listId || productId;
          if (!docId) return;

          const favoriteRef = doc(db, "users", userId, "favorites", docId);
          const favoriteSnap = await getDoc(favoriteRef);
          setIsFavorite(favoriteSnap.exists());
        } catch (error) {
          console.error("Error checking favorites:", error);
        }
      }
    };
    checkIfFavorite();
  }, [userId, listId, productId]);

  const toggleFavorite = async () => {
    if (!userId) {
      toast.error("Please log in to manage favorites");
      return;
    }

    try {
      // Use productId if listId is not available
      const docId = listId || productId;
      if (!docId) return;

      const favoriteRef = doc(db, "users", userId, "favorites", docId);

      if (isFavorite) {
        // Remove from favorites (hard delete)
        setIsFavorite(false);
        await deleteDoc(favoriteRef);

        toast.success("Removed from favorites", toastOptions);
      } else {
        // Add to favorites

        const favoriteData = product
          ? {
            timestamp: serverTimestamp(),
            products: [product],
            productIds: [product.id],
            type,
          }
          : {
            listId,
            listName,
            timestamp: serverTimestamp(),
            products: list?.products || [],
            productIds: list?.products?.map((product) => product.id) || [],
            type,
            id: list?.id,
          };

        setIsFavorite(true);
        await setDoc(favoriteRef, favoriteData);

        toast.success("Added to favorites", toastOptions);
        await awardPoints('addToFavorites')
        posthog.capture('addedToFavorites',{
          'product':`${product?.id}`
        })
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error(
        `Failed to ${isFavorite ? "remove from" : "add to"} favorites`,
        toastOptions
      );
    }
  };

  return (
    <div
      className="d-inline-flex d-flex justify-content-center align-items-center"
      onClick={(e) => {
        e.stopPropagation(); // Prevent event bubbling
        toggleFavorite();
      }}
      style={{
        cursor: "pointer", zIndex: 10,
        width: width,
        height: height,
        backgroundColor: "#FFFFFF",
        borderRadius: borderRadius
      }}
      title={isFavorite ? "Remove from favorites" : "Add to favorites"}
    >
      { isFavorite ? (
        // Show filled/favorite SVG
        componentName === 'similarProducts' ? (
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.82993 1.54347C8.04214 1.06737 8.14829 0.829325 8.29605 0.756019C8.42439 0.692329 8.57509 0.692329 8.70342 0.756019C8.85119 0.829325 8.95734 1.06737 9.16955 1.54347L10.8599 5.33566C10.9227 5.4764 10.954 5.54678 11.0026 5.60067C11.0455 5.64835 11.098 5.68649 11.1566 5.7126C11.2229 5.74212 11.2995 5.7502 11.4528 5.76637L15.5817 6.20216C16.1001 6.25687 16.3592 6.28422 16.4746 6.4021C16.5748 6.50448 16.6214 6.64785 16.6005 6.7896C16.5765 6.95277 16.3829 7.12721 15.9957 7.47618L12.9114 10.2557C12.797 10.3588 12.7397 10.4104 12.7035 10.4732C12.6714 10.5289 12.6513 10.5906 12.6446 10.6544C12.637 10.7265 12.653 10.8019 12.685 10.9526L13.5464 15.0141C13.6546 15.5241 13.7087 15.779 13.6322 15.9251C13.5658 16.0522 13.4439 16.1407 13.3026 16.1646C13.14 16.1921 12.9142 16.062 12.4627 15.8015L8.86613 13.7271C8.73266 13.6501 8.66593 13.6117 8.59498 13.5966C8.53219 13.5833 8.46729 13.5833 8.40449 13.5966C8.33354 13.6117 8.26681 13.6501 8.13334 13.7271L4.53682 15.8015C4.08529 16.062 3.85952 16.1921 3.6969 16.1646C3.55563 16.1407 3.43368 16.0522 3.36727 15.9251C3.29083 15.779 3.34491 15.5241 3.45306 15.0141L4.31451 10.9526C4.34648 10.8019 4.36247 10.7265 4.35488 10.6544C4.34816 10.5906 4.32811 10.5289 4.29603 10.4732C4.25978 10.4104 4.20254 10.3588 4.08808 10.2557L1.00384 7.47618C0.616631 7.12721 0.423022 6.95277 0.398951 6.7896C0.378051 6.64785 0.424627 6.50448 0.524855 6.4021C0.640245 6.28422 0.899432 6.25687 1.41781 6.20216L5.54674 5.76637C5.69999 5.7502 5.7766 5.74212 5.84286 5.7126C5.90148 5.68649 5.95397 5.64835 5.99692 5.60067C6.04546 5.54678 6.07683 5.4764 6.13958 5.33566L7.82993 1.54347Z" fill="#005FDA" />
          </svg>

        ) : (
          <svg
            width='16'
            height='16'
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.51277 2.94078C7.66711 2.59452 7.74431 2.4214 7.85177 2.36808C7.94511 2.32176 8.05471 2.32176 8.14804 2.36808C8.25551 2.4214 8.33271 2.59452 8.48704 2.94078L9.71637 5.69873C9.76204 5.80109 9.78484 5.85227 9.82017 5.89146C9.85137 5.92614 9.88957 5.95388 9.93217 5.97287C9.98037 5.99434 10.0361 6.00022 10.1476 6.01198L13.1504 6.32892C13.5274 6.3687 13.7159 6.3886 13.7998 6.47432C13.8727 6.54878 13.9066 6.65305 13.8914 6.75614C13.8739 6.87481 13.7331 7.00168 13.4515 7.25548L11.2084 9.27694C11.1252 9.35194 11.0835 9.38948 11.0572 9.43514C11.0338 9.47561 11.0192 9.52048 11.0144 9.56688C11.0088 9.61934 11.0204 9.67414 11.0437 9.78381L11.6702 12.7376C11.7489 13.1085 11.7882 13.2939 11.7326 13.4001C11.6843 13.4925 11.5956 13.5569 11.4929 13.5743C11.3746 13.5943 11.2104 13.4997 10.882 13.3103L8.26637 11.8016C8.16931 11.7456 8.12077 11.7177 8.06917 11.7067C8.02351 11.697 7.97631 11.697 7.93064 11.7067C7.87904 11.7177 7.83051 11.7456 7.73344 11.8016L5.11779 13.3103C4.7894 13.4997 4.62521 13.5943 4.50693 13.5743C4.40419 13.5569 4.3155 13.4925 4.26721 13.4001C4.21161 13.2939 4.25094 13.1085 4.3296 12.7376L4.95611 9.78381C4.97936 9.67414 4.99099 9.61934 4.98547 9.56688C4.98058 9.52048 4.966 9.47561 4.94267 9.43514C4.9163 9.38948 4.87467 9.35194 4.79143 9.27694L2.54835 7.25548C2.26674 7.00168 2.12593 6.87481 2.10843 6.75614C2.09323 6.65305 2.1271 6.54878 2.19999 6.47432C2.28391 6.3886 2.47241 6.3687 2.84941 6.32892L5.85227 6.01198C5.96373 6.00022 6.01945 5.99434 6.06763 5.97287C6.11027 5.95388 6.14844 5.92614 6.17968 5.89146C6.21498 5.85227 6.23779 5.80109 6.28343 5.69873L7.51277 2.94078Z"
              fill="#005FDA"
            />
          </svg>
        )
      ) : (
        componentName === 'similarProducts' ? (
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.82993 1.54347C8.04214 1.06737 8.14829 0.829325 8.29605 0.756019C8.42439 0.692329 8.57509 0.692329 8.70342 0.756019C8.85119 0.829325 8.95734 1.06737 9.16955 1.54347L10.8599 5.33566C10.9227 5.4764 10.954 5.54678 11.0026 5.60067C11.0455 5.64835 11.098 5.68649 11.1566 5.7126C11.2229 5.74212 11.2995 5.7502 11.4528 5.76637L15.5817 6.20216C16.1001 6.25687 16.3592 6.28422 16.4746 6.4021C16.5748 6.50448 16.6214 6.64785 16.6005 6.7896C16.5765 6.95277 16.3829 7.12721 15.9957 7.47618L12.9114 10.2557C12.797 10.3588 12.7397 10.4104 12.7035 10.4732C12.6714 10.5289 12.6513 10.5906 12.6446 10.6544C12.637 10.7265 12.653 10.8019 12.685 10.9526L13.5464 15.0141C13.6546 15.5241 13.7087 15.779 13.6322 15.9251C13.5658 16.0522 13.4439 16.1407 13.3026 16.1646C13.14 16.1921 12.9142 16.062 12.4627 15.8015L8.86613 13.7271C8.73266 13.6501 8.66593 13.6117 8.59498 13.5966C8.53219 13.5833 8.46729 13.5833 8.40449 13.5966C8.33354 13.6117 8.26681 13.6501 8.13334 13.7271L4.53682 15.8015C4.08529 16.062 3.85952 16.1921 3.6969 16.1646C3.55563 16.1407 3.43368 16.0522 3.36727 15.9251C3.29083 15.779 3.34491 15.5241 3.45306 15.0141L4.31451 10.9526C4.34648 10.8019 4.36247 10.7265 4.35488 10.6544C4.34816 10.5906 4.32811 10.5289 4.29603 10.4732C4.25978 10.4104 4.20254 10.3588 4.08808 10.2557L1.00384 7.47618C0.616631 7.12721 0.423022 6.95277 0.398951 6.7896C0.378051 6.64785 0.424627 6.50448 0.524855 6.4021C0.640245 6.28422 0.899432 6.25687 1.41781 6.20216L5.54674 5.76637C5.69999 5.7502 5.7766 5.74212 5.84286 5.7126C5.90148 5.68649 5.95397 5.64835 5.99692 5.60067C6.04546 5.54678 6.07683 5.4764 6.13958 5.33566L7.82993 1.54347Z" fill="#DEDEDE" />
          </svg>


        ) : (
          // Show outlined/empty SVG
          <svg
            width='16'
            height='16'
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.51277 2.94078C7.66711 2.59452 7.74431 2.4214 7.85177 2.36808C7.94511 2.32176 8.05471 2.32176 8.14804 2.36808C8.25551 2.4214 8.33271 2.59452 8.48704 2.94078L9.71637 5.69873C9.76204 5.80109 9.78484 5.85227 9.82017 5.89146C9.85137 5.92614 9.88957 5.95388 9.93217 5.97287C9.98037 5.99434 10.0361 6.00022 10.1476 6.01198L13.1504 6.32892C13.5274 6.3687 13.7159 6.3886 13.7998 6.47432C13.8727 6.54878 13.9066 6.65305 13.8914 6.75614C13.8739 6.87481 13.7331 7.00168 13.4515 7.25548L11.2084 9.27694C11.1252 9.35194 11.0835 9.38948 11.0572 9.43514C11.0338 9.47561 11.0192 9.52048 11.0144 9.56688C11.0088 9.61934 11.0204 9.67414 11.0437 9.78381L11.6702 12.7376C11.7489 13.1085 11.7882 13.2939 11.7326 13.4001C11.6843 13.4925 11.5956 13.5569 11.4929 13.5743C11.3746 13.5943 11.2104 13.4997 10.882 13.3103L8.26637 11.8016C8.16931 11.7456 8.12077 11.7177 8.06917 11.7067C8.02351 11.697 7.97631 11.697 7.93064 11.7067C7.87904 11.7177 7.83051 11.7456 7.73344 11.8016L5.11779 13.3103C4.7894 13.4997 4.62521 13.5943 4.50693 13.5743C4.40419 13.5569 4.3155 13.4925 4.26721 13.4001C4.21161 13.2939 4.25094 13.1085 4.3296 12.7376L4.95611 9.78381C4.97936 9.67414 4.99099 9.61934 4.98547 9.56688C4.98058 9.52048 4.966 9.47561 4.94267 9.43514C4.9163 9.38948 4.87467 9.35194 4.79143 9.27694L2.54835 7.25548C2.26674 7.00168 2.12593 6.87481 2.10843 6.75614C2.09323 6.65305 2.1271 6.54878 2.19999 6.47432C2.28391 6.3886 2.47241 6.3687 2.84941 6.32892L5.85227 6.01198C5.96373 6.00022 6.01945 5.99434 6.06763 5.97287C6.11027 5.95388 6.14844 5.92614 6.17968 5.89146C6.21498 5.85227 6.23779 5.80109 6.28343 5.69873L7.51277 2.94078Z"
              fill="#DEDEDE"
            />
          </svg>
        ))}
    </div>
  );
};

export default AddToFavorites;
